import React, { createContext, useContext, useState, useEffect } from 'react';
import logger from '../utils/logger';
import { useAuth } from './AuthContext';

const TokenContext = createContext();

export const useToken = () => {
  const context = useContext(TokenContext);
  if (!context) {
    throw new Error('useToken must be used within a TokenProvider');
  }
  return context;
};

export const TokenProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [tokenClaims, setTokenClaims] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const refreshToken = async () => {
    if (!currentUser) {
      return;
    }

    try {
      setIsLoading(true);
      const token = await currentUser.getIdTokenResult();
      setTokenClaims(token.claims);
      setError(null);
    } catch (error) {
      logger.error('Error refreshing token:', error);
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Initial token fetch
  useEffect(() => {
    if (currentUser && !tokenClaims) {
      refreshToken();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Refresh token every 5 minutes
  useEffect(() => {
    if (!currentUser) {
      return;
    }

    const interval = setInterval(
      () => {
        refreshToken();
      },
      5 * 60 * 1000
    ); // 5 minutes

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const value = {
    tokenClaims,
    isLoading,
    error,
    refreshToken
  };

  return <TokenContext.Provider value={value}>{children}</TokenContext.Provider>;
};
